/*
 * Sample churn, exit and win-back data (P7). A customer at risk carries the
 * triggers that flagged it, a retention plan and every save attempt; an exit
 * runs Requested → Account review → Approved → Checklist → Deactivated →
 * Confirmed; a former customer gets a re-engagement date and, when the time
 * is right, a win-back opportunity.
 */
import { daysFromNow } from "@/helpers/crm";

/* ---------- Masters kept under Settings → Customer Success ---------- */

export const churnTriggers = [
  { key: "usage", name: "Usage drop", rule: "Active users down ≥ 25% in 30 days", icon: "iconoir-graph-down" },
  { key: "engagement", name: "Low engagement", rule: "No CS engagement in 30 days or missed QBR", icon: "iconoir-chat-bubble" },
  { key: "payment", name: "Payment delay", rule: "Invoice > 45 days overdue", icon: "iconoir-wallet" },
  { key: "complaint", name: "Complaint", rule: "High/critical complaint open, or 2+ in 90 days", icon: "iconoir-warning-triangle" },
  { key: "expiry", name: "Contract expiry", rule: "Renewal within 60 days and not confirmed", icon: "iconoir-calendar" },
  { key: "competitor", name: "Competitor activity", rule: "Competitor mentioned in an engagement", icon: "iconoir-shield" },
];
export const triggerByKey = (k) => churnTriggers.find((t) => t.key === k);

// Score bands (Phase 2): Low < 30, Medium 30–54, High 55–74, Critical ≥ 75.
export const riskScoreLevel = (score) => (score >= 75 ? "critical" : score >= 55 ? "high" : score >= 30 ? "medium" : "low");
// High-value accounts escalate to management when the risk is High or Critical.
export const managementThreshold = 600000; // ARR, KES

export const playbooks = [
  { key: "recovery", name: "Service recovery", for: "Complaint / support failures", steps: ["Executive apology call within 48h", "Root-cause report to customer", "Weekly status until stable", "Service credit if SLA breached"], owner: "Customer Success", success_rate: 62 },
  { key: "adoption", name: "Re-adoption", for: "Usage drop", steps: ["Usage review with admin", "Targeted retraining", "Enable unused modules", "30-day usage check"], owner: "Customer Success", success_rate: 71 },
  { key: "commercial", name: "Commercial save", for: "Price / budget / competitor", steps: ["Value review (ROI)", "Right-size package", "Multi-year discount (approval)", "Management call"], owner: "Account Manager", success_rate: 48 },
  { key: "collections", name: "Payment recovery", for: "Payment delay", steps: ["Finance reminder", "Payment plan proposal", "Service-hold warning (approval)"], owner: "Finance", success_rate: 80 },
];

/* ---------- Churn risk ---------- */

export const churnRisks = [
  {
    id: 1, customer_id: 3, flag: "critical", score: 86, probability: 72, owner_id: 7, playbook: "recovery",
    triggers: [
      { key: "usage", detail: "Active users 212 → 66 in 30 days", at: daysFromNow(-12) },
      { key: "complaint", detail: "CMP-0021 critical, escalated", at: daysFromNow(-3) },
      { key: "competitor", detail: "SageHR quote mentioned on call", at: daysFromNow(-6) },
      { key: "expiry", detail: "Renewal in 45 days, not confirmed", at: daysFromNow(-15) },
    ],
    plan: [
      { action: "On-site recovery meeting", owner_id: 7, due: daysFromNow(-2), status: "Done" },
      { action: "Root-cause report for payroll failures", owner_id: 6, due: daysFromNow(2), status: "In progress" },
      { action: "Weekly status call until 3 clean runs", owner_id: 7, due: daysFromNow(5), status: "Open" },
      { action: "Service credit (1 month) — goes for approval", owner_id: 2, due: daysFromNow(7), status: "Pending approval" },
    ],
    saves: [
      { date: daysFromNow(-6), by: 7, offer: "Dedicated payroll specialist for next 3 runs", outcome: "Accepted" },
      { date: daysFromNow(-2), by: 2, offer: "1 month service credit + 2027 price freeze", outcome: "Considering" },
    ],
    escalation: { to: "Brian Otieno (Head of Sales) & Grace Njeri (Head of CS)", at: daysFromNow(-3), note: "High-value at critical risk" },
  },
  {
    id: 2, customer_id: 4, flag: "at_risk", score: 64, probability: 48, owner_id: 10, playbook: "commercial",
    triggers: [
      { key: "payment", detail: "INV-2231 64 days overdue (KES 103,000)", at: daysFromNow(-19) },
      { key: "complaint", detail: "CMP-0024 billing dispute", at: daysFromNow(-9) },
      { key: "expiry", detail: "Renewal in 45 days", at: daysFromNow(-15) },
    ],
    plan: [
      { action: "Billing reconciliation & credit note", owner_id: 11, due: daysFromNow(-9), status: "Done" },
      { action: "Month-end payroll performance review", owner_id: 8, due: daysFromNow(3), status: "Open" },
      { action: "Renewal options call", owner_id: 10, due: daysFromNow(2), status: "Open" },
    ],
    saves: [{ date: daysFromNow(-9), by: 10, offer: "Credit note KES 78,000 for over-billing", outcome: "Accepted" }],
    escalation: null,
  },
  {
    id: 3, customer_id: 16, flag: "at_risk", score: 52, probability: 30, owner_id: 10, playbook: "recovery",
    triggers: [{ key: "complaint", detail: "CMP-0027 payslips to wrong recipients", at: daysFromNow(-4) }, { key: "usage", detail: "Active users down 25%", at: daysFromNow(-2) }],
    plan: [{ action: "Incident report & DPO call", owner_id: 10, due: daysFromNow(1), status: "In progress" }],
    saves: [],
    escalation: null,
  },
  {
    id: 4, customer_id: 6, flag: "at_risk", score: 38, probability: 18, owner_id: 10, playbook: "recovery",
    triggers: [{ key: "complaint", detail: "Two SLA breaches in 14 days", at: daysFromNow(-8) }],
    plan: [{ action: "Team Lead as named contact for 60 days", owner_id: 8, due: daysFromNow(-8), status: "Done" }],
    saves: [{ date: daysFromNow(-8), by: 8, offer: "Named support contact", outcome: "Accepted" }],
    escalation: null,
  },
  {
    id: 5, customer_id: 5, flag: "normal", score: 28, probability: 12, owner_id: 7, playbook: "adoption",
    triggers: [{ key: "engagement", detail: "No CS engagement in 38 days", at: daysFromNow(-1) }],
    plan: [{ action: "Check-in call", owner_id: 7, due: daysFromNow(1), status: "Open" }],
    saves: [],
    escalation: null,
  },
  {
    id: 6, customer_id: 12, flag: "critical", score: 95, probability: 98, owner_id: 10, playbook: "commercial",
    triggers: [{ key: "expiry", detail: "Exit requested; renewal in 20 days", at: daysFromNow(-18) }, { key: "usage", detail: "Usage down 50%", at: daysFromNow(-30) }],
    plan: [{ action: "Offer reduced 'NGO Lite' package", owner_id: 10, due: daysFromNow(-16), status: "Done" }],
    saves: [{ date: daysFromNow(-16), by: 10, offer: "50% discount for 6 months", outcome: "Declined — funding ended" }],
    escalation: null,
  },
];

/* ---------- Exits ---------- */

export const exitStages = [
  { key: "requested", name: "Requested", kind: "open", order: 1 },
  { key: "review", name: "Account review", kind: "open", order: 2 },
  { key: "approved", name: "Approved", kind: "open", order: 3 },
  { key: "checklist", name: "Checklist", kind: "open", order: 4 },
  { key: "deactivated", name: "Deactivated", kind: "open", order: 5 },
  { key: "confirmed", name: "Confirmed", kind: "won", order: 6 },
];

// The exit checklist template (Settings → Customer Success → Exit checklist).
export const exitChecklistTemplate = [
  { key: "payment", area: "Payment", item: "All invoices settled or written off (Finance)", owner_id: 11 },
  { key: "data", area: "Data", item: "Data export delivered and receipt confirmed by customer", owner_id: 6 },
  { key: "handback", area: "Data", item: "Data retention / deletion schedule agreed (DPA)", owner_id: 6 },
  { key: "users", area: "Users", item: "Customer users disabled", owner_id: 8 },
  { key: "access", area: "Access", item: "Connected apps revoked, API tokens & SSO removed", owner_id: 8 },
  { key: "contract", area: "Contract", item: "Termination letter issued; contract closed", owner_id: 1 },
  { key: "assets", area: "Assets", item: "Product access withdrawn: tenant suspended / standalone install handed back, support token revoked", owner_id: 6 },
];

const checklist = (doneKeys) =>
  exitChecklistTemplate.map((c, i) => ({ ...c, done: doneKeys.includes(c.key), done_on: doneKeys.includes(c.key) ? daysFromNow(-10 + i) : null }));

const approvals = (state) => [
  { level: 1, role: "Customer Success Manager", approver: "Samuel Kariuki", status: "approved", at: daysFromNow(-15), comment: "Save attempts exhausted." },
  { level: 2, role: "Finance", approver: "Mercy Wambui", status: state === "review" ? "pending" : "approved", at: state === "review" ? null : daysFromNow(-13), comment: state === "review" ? "" : "Outstanding balance to be cleared by exit date." },
  { level: 3, role: "Head of Customer Success", approver: "Grace Njeri", status: ["review", "requested"].includes(state) ? "pending" : "approved", at: ["review", "requested"].includes(state) ? null : daysFromNow(-12), comment: "" },
];

export const exits = [
  {
    id: 7, number: "EXT-0007", customer_id: 12, requested_on: daysFromNow(-18), exit_date: daysFromNow(20), reason_code: "BUS-02",
    requested_by: "Esther Wairimu (Programs Lead) by letter", stage: "checklist", owner_id: 10, value: 216000,
    note: "Donor programme ended; NGO merging into a partner organisation's systems.",
    review: {
      obligations: "12-month term ends in 20 days; 30-day notice given (met).",
      contract_terms: "Data export in CSV + PDF within 14 days of request; data deleted 90 days after exit.",
      invoices: [{ no: "INV-2290", amount: 18000, due: daysFromNow(-5), status: "Overdue" }, { no: "INV-2321", amount: 18000, due: daysFromNow(10), status: "Open" }],
    },
    approvals: approvals("checklist"),
    checklist: checklist(["data", "contract", "assets"]),
    survey: null, reengage_on: null, winback_created: false,
  },
  {
    id: 8, number: "EXT-0008", customer_id: 16, requested_on: daysFromNow(-2), exit_date: daysFromNow(58), reason_code: "SRV-01",
    requested_by: "Victor Mutua (CFO) by email", stage: "review", owner_id: 10, value: 300000,
    note: "Notice after payslip privacy incident. Save attempt under way — the request is held in review, not yet approved.",
    review: { obligations: "Term ends in 60 days; 60-day notice required (met).", contract_terms: "Standard SME terms.", invoices: [] },
    approvals: approvals("review"),
    checklist: checklist([]),
    survey: null, reengage_on: null, winback_created: false,
  },
  {
    id: 5, number: "EXT-0005", customer_id: 14, requested_on: daysFromNow(-100), exit_date: daysFromNow(-60), reason_code: "CMP-01", competitor: "SageHR",
    requested_by: "Fatma Said (GM)", stage: "confirmed", owner_id: 10, value: 720000,
    note: "Moved to SageHR bundled with their new PMS vendor at a lower price.",
    review: { obligations: "Term ended; no penalties.", contract_terms: "Standard mid-market terms.", invoices: [] },
    approvals: approvals("confirmed"),
    checklist: checklist(exitChecklistTemplate.map((c) => c.key)),
    survey: { satisfaction: 3, nps: 5, comments: "Product was fine; booking integration issues and price drove the switch." },
    reengage_on: daysFromNow(120), winback_created: false,
  },
  {
    id: 3, number: "EXT-0003", customer_id: 17, requested_on: daysFromNow(-150), exit_date: daysFromNow(-120), reason_code: "BUS-01",
    requested_by: "Dr. Nancy Mbugua (Owner)", stage: "confirmed", owner_id: 7, value: 216000,
    note: "Practice closed after the owner relocated.",
    review: { obligations: "Early termination fee waived (business closure).", contract_terms: "Standard SME terms.", invoices: [] },
    approvals: approvals("confirmed"),
    checklist: checklist(exitChecklistTemplate.map((c) => c.key)),
    survey: { satisfaction: 4, nps: 8, comments: "Would use again if we reopen." },
    reengage_on: daysFromNow(240), winback_created: false,
  },
];
export const exitById = (id) => exits.find((e) => e.id === Number(id));

// Exits from earlier years, for the analysis only (their customers were archived before this prototype's sample set).
export const exitHistory = [
  { name: "Lakeview Courier Services", industry: "Logistics", segment: "SME", product_id: 1, reason_code: "PRC-02", value: 300000, exited_on: daysFromNow(-200) },
  { name: "Mvita Clinics", industry: "Healthcare", segment: "MID", product_id: 1, reason_code: "SRV-02", value: 600000, exited_on: daysFromNow(-260) },
  { name: "Safari Tours Ltd", industry: "Hospitality", segment: "SME", product_id: 1, reason_code: "CMP-02", competitor: "Zoho", value: 216000, exited_on: daysFromNow(-310) },
  { name: "Sokoni Wholesalers", industry: "Retail", segment: "MID", product_id: 1, reason_code: "PRD-01", value: 468000, exited_on: daysFromNow(-330) },
  { name: "Kisumu Water Co.", industry: "Government", segment: "ENT", product_id: 1, reason_code: "BUS-02", value: 1440000, exited_on: daysFromNow(-400) },
  { name: "Thika Fresh Dairies", industry: "Manufacturing", segment: "MID", product_id: 1, reason_code: "PRC-02", value: 720000, exited_on: daysFromNow(-280) },
];

/* ---------- Win-back ---------- */

export const winBackStages = ["Re-engage", "Conversation", "Proposal", "Won back", "Not now"];

export const formerCustomers = [
  { id: 1, customer_id: 14, name: "Nyali Beach Resort", reason_code: "CMP-01", exited_on: daysFromNow(-60), reengage_on: daysFromNow(120), probability: 35, last_value: 720000, owner_id: 4, note: "SageHR contract is 12 months; their PMS vendor integration is weak." },
  { id: 2, customer_id: 17, name: "Westlands Dental", reason_code: "BUS-01", exited_on: daysFromNow(-120), reengage_on: daysFromNow(240), probability: 10, last_value: 216000, owner_id: 3, note: "Owner may reopen in 2027." },
  { id: 3, customer_id: null, name: "Lakeview Courier Services", reason_code: "PRC-02", exited_on: daysFromNow(-200), reengage_on: daysFromNow(-5), probability: 55, last_value: 300000, owner_id: 4, note: "New Basic package now fits their budget." },
  { id: 4, customer_id: null, name: "Mvita Clinics", reason_code: "SRV-02", exited_on: daysFromNow(-260), reengage_on: daysFromNow(-40), probability: 40, last_value: 600000, owner_id: 1, note: "New implementation team since; offer a pilot." },
  { id: 5, customer_id: null, name: "Safari Tours Ltd", reason_code: "CMP-02", exited_on: daysFromNow(-310), reengage_on: daysFromNow(15), probability: 25, last_value: 216000, owner_id: 3, note: "Zoho renewal due in 3 months." },
  { id: 6, customer_id: null, name: "Sokoni Wholesalers", reason_code: "PRD-01", exited_on: daysFromNow(-330), reengage_on: daysFromNow(-60), probability: 65, last_value: 468000, owner_id: 5, note: "Missing casual-worker payroll — now planned (FR-012)." },
  { id: 7, customer_id: null, name: "Thika Fresh Dairies", reason_code: "PRC-02", exited_on: daysFromNow(-280), reengage_on: daysFromNow(-90), probability: 80, last_value: 720000, owner_id: 1, note: "Won back on 2-year deal." },
];

export const winBackOpportunities = [
  { id: 1, former_id: 7, code: "OPP-0141", stage: "Won back", value: 660000, owner_id: 1, opened: daysFromNow(-85), closed: daysFromNow(-30) },
  { id: 2, former_id: 6, code: "OPP-0152", stage: "Proposal", value: 520000, owner_id: 5, opened: daysFromNow(-55), closed: null },
  { id: 3, former_id: 4, code: "OPP-0149", stage: "Conversation", value: 600000, owner_id: 1, opened: daysFromNow(-38), closed: null },
  { id: 4, former_id: 3, code: "OPP-0158", stage: "Re-engage", value: 300000, owner_id: 4, opened: daysFromNow(-4), closed: null },
];
